// The slice of GitHub's API sync needs, all of it on one branch: notes live
// on an orphan branch of their own, apart from whatever else the repo holds.
// Every write names the blob
// sha it expects to replace, so GitHub itself refuses a write over a change
// this device hasn't seen - that refusal is the conflict detection.
import {
  fromBase64,
  toBase64,
} from './base64';

export interface RepoConfig {
  // "owner/name"
  repo: string;
  token: string;
  branch: string;
}

export const DEFAULT_BRANCH = 'data';

export interface RemoteFile {
  name: string;
  sha: string;
}

export class ConflictError extends Error {}

// A text file's contents. GitHub base64s them, as UTF-8.
const decodeContent = (base64: string) =>
  new TextDecoder().decode(fromBase64(base64.replace(/\n/g, '')));

const encodeContent = (text: string) =>
  toBase64(new TextEncoder().encode(text));

// A connection that hangs rather than fails would otherwise hold a sync -
// and the boot, waiting on the keycheck - forever. An upload gets longer by
// its size: aborting a slow one frees nothing, as the bytes already handed to
// the browser stay queued on its connection ahead of every later request, and
// the retry queues them again behind the first.
const TIMEOUT_MS = 20_000;
const UPLOAD_TIMEOUT_MAX_MS = 180_000;
// a floor a working connection clears; below it the upload is as good as hung
const UPLOAD_BYTES_PER_S = 40_000;

export const timeoutFor = (base: number, body: RequestInit['body']) =>
  typeof body === 'string'
    ? Math.min(
      Math.round(base + body.length / UPLOAD_BYTES_PER_S * 1000),
      UPLOAD_TIMEOUT_MAX_MS,
    )
    : base;

export class TimeoutError extends Error {}

// commits per page of history, the most GitHub gives
const PER_PAGE = 100;

// A commit's message: what it did, and to which file
export const message = (verb: 'update' | 'delete', path: string) =>
  `${verb} ${path}`;

// A response read whole, under the request's timeout
interface Reply {
  ok: boolean;
  status: number;
  text: string;
  json(): any;
}

export class GitHub {
  constructor(
    readonly config: RepoConfig,
    private readonly fetchImpl: typeof fetch = fetch.bind(globalThis),
    private readonly timeoutMs = TIMEOUT_MS,
  ) {}

  private contents(path: string, init: RequestInit = {}) {
    const ref = init.method ? '' : `?ref=${this.config.branch}`;
    return this.request(`contents/${path}${ref}`, init);
  }

  private body(fields: Record<string, unknown>) {
    return JSON.stringify({ ...fields, branch: this.config.branch });
  }

  private async request(path: string, init: RequestInit = {}): Promise<Reply> {
    const timeoutMs = timeoutFor(this.timeoutMs, init.body);
    try {
      const res = await this.fetchImpl(
        `https://api.github.com/repos/${this.config.repo}/${path}`,
        {
          ...init,
          signal: init.signal ?? AbortSignal.timeout(timeoutMs),
          // GitHub sends `max-age=60` on reads; a cached listing would hide
          // what another device pushed a moment ago
          cache: 'no-store',
          headers: {
            'Accept': 'application/vnd.github+json',
            'Authorization': `Bearer ${this.config.token}`,
            'X-GitHub-Api-Version': '2022-11-28',
            ...init.headers,
          },
        },
      );
      // read here, under the same signal, so a body that stalls times out too
      const text = await res.text();
      return {
        ok: res.ok,
        status: res.status,
        text,
        json: () => JSON.parse(text),
      };
    } catch (e) {
      // AbortSignal.timeout's "signal timed out" says nothing of what hung
      if ((e as { name?: string } | null)?.name !== 'TimeoutError') throw e;
      throw new TimeoutError(
        `${init.method ?? 'GET'} ${path.split('?')[0]}: no answer in `
          + `${Math.round(timeoutMs / 1000)}s`,
        { cause: e },
      );
    }
  }

  private fail(res: Reply, what: string): never {
    // 409: the sha is stale. 422: no sha was sent for a file that exists.
    if (res.status === 409 || res.status === 422) {
      throw new ConflictError(`${what}: remote changed`);
    }
    throw new Error(`${what}: ${res.status} ${res.text}`);
  }

  // An orphan branch whose only commit is a README, for a repo that doesn't
  // have the notes branch yet. Writes through the contents API can only go
  // to a branch that exists.
  async ensureBranch() {
    const { branch } = this.config;
    const existing = await this.request(`git/ref/heads/${branch}`);
    if (existing.ok) return;
    if (existing.status !== 404) return this.fail(existing, `ref ${branch}`);

    const post = async (path: string, fields: object) => {
      const res = await this.request(path, {
        method: 'POST',
        body: JSON.stringify(fields),
      });
      if (!res.ok) return this.fail(res, `create ${branch}`);
      return res.json().sha as string;
    };
    const tree = await post('git/trees', {
      tree: [{
        path: 'README.md',
        mode: '100644',
        type: 'blob',
        content: 'Encrypted notes. Every file under notes/ is one note.\n',
      }],
    });
    const commit = await post('git/commits', {
      message: 'notes branch',
      tree,
      parents: [],
    });
    await post('git/refs', { ref: `refs/heads/${branch}`, sha: commit });
  }

  // Through the trees API: a contents listing stops at 1000 files, silently,
  // and a file left out would read as deleted. So would every file on a
  // branch that's gone, which is an error instead - as is a token that can
  // no longer read the repo, which GitHub answers with the same 404. Null for
  // a directory that isn't there.
  async list(dir: string): Promise<RemoteFile[] | null> {
    const { branch } = this.config;
    const root = await this.tree(branch);
    if (!root) {
      throw new Error(
        `branch ${branch} is missing or out of the token's reach - check `
          + 'the repo and token, or unlock again to create it',
      );
    }
    const sub = root.find(e => e.type === 'tree' && e.path === dir);
    if (!sub) return null;
    return ((await this.tree(sub.sha)) ?? [])
      .filter(e => e.type === 'blob')
      .map(({ path, sha }) => ({ name: path, sha }));
  }

  private async tree(ref: string) {
    const res = await this.request(`git/trees/${ref}`);
    if (res.status === 404) return null;
    if (!res.ok) return this.fail(res, `tree ${ref}`);
    const { tree, truncated } = res.json();
    if (truncated) throw new Error(`tree ${ref}: truncated`);
    return tree as { path: string, type: string, sha: string }[];
  }

  // The files under `dir` written or removed lately, newest first, read off
  // the messages of the last `pages` hundred commits to it: each names the
  // file it touched (`message`). Commits from before they did name none.
  async recent(dir: string, pages = 3): Promise<string[]> {
    const { branch } = this.config;
    const prefix = `${dir}/`;
    const names: string[] = [];
    for (let page = 1; page <= pages; page++) {
      const res = await this.request(
        `commits?sha=${branch}&path=${dir}&per_page=${PER_PAGE}&page=${page}`,
      );
      if (!res.ok) return this.fail(res, `commits ${dir}`);
      const commits: { commit: { message: string } }[] = res.json();
      names.push(
        ...commits
          .map(c => c.commit.message.split(' ')[1] ?? '')
          .filter(path => path.startsWith(prefix))
          .map(path => path.slice(prefix.length)),
      );
      if (commits.length < PER_PAGE) break;
    }
    return [...new Set(names)];
  }

  async read(path: string): Promise<{ text: string, sha: string } | null> {
    const res = await this.contents(path);
    if (res.status === 404) return null;
    if (!res.ok) return this.fail(res, `read ${path}`);
    const { content, encoding, sha } = res.json();
    // over 1 MB the contents API leaves the content out; the blob API has it
    if (encoding === 'none') return { text: await this.blob(sha), sha };
    return { text: decodeContent(content), sha };
  }

  private async blob(sha: string) {
    const res = await this.request(`git/blobs/${sha}`);
    if (!res.ok) return this.fail(res, `blob ${sha}`);
    return decodeContent(res.json().content);
  }

  // Returns the new blob sha.
  async write(
    path: string,
    text: string,
    sha: string | null,
    message: string,
  ): Promise<string> {
    const res = await this.contents(path, {
      method: 'PUT',
      body: this.body({
        message,
        content: encodeContent(text),
        ...(sha ? { sha } : {}),
      }),
    });
    // the file this sha names is gone: the remote moved on, as with a stale sha
    if (res.status === 404 && sha) {
      throw new ConflictError(`write ${path}: remote gone`);
    }
    if (!res.ok) return this.fail(res, `write ${path}`);
    return res.json().content.sha;
  }

  async remove(path: string, sha: string, message: string): Promise<void> {
    const res = await this.contents(path, {
      method: 'DELETE',
      body: this.body({ message, sha }),
    });
    // already gone is what we wanted
    if (res.status === 404) return;
    if (!res.ok) return this.fail(res, `remove ${path}`);
  }
}
